/**
 * API Target Configuration
 *
 * Allows QA to test different API backends by manually setting a value in localStorage.
 * The application reads this value but does not auto-initialize it.
 *
 * Usage (QA only):
 *   // Set via browser console:
 *   localStorage.setItem('API_TARGET', 'cxc');
 *
 *   // Clear to revert to default:
 *   localStorage.removeItem('API_TARGET');
 *
 * Valid values: amplify, amplify-all, cxc
 *
 * When API_TARGET is not in localStorage, the X-API-Target header will not be sent.
 */

/**
 * Valid API target values
 */
export const VALID_TARGETS = ['amplify', 'amplify-all', 'cxc'];

/**
 * localStorage key for API target
 */
export const STORAGE_KEY = 'API_TARGET';

/**
 * HTTP header name for API target
 */
export const HEADER_NAME = 'X-API-Target';

/**
 * Check if a string is a valid API target
 */
export function isValidTarget(value) {
  return VALID_TARGETS.includes(value);
}

/**
 * Get the current API target from localStorage.
 * Returns null if not set or invalid — no header will be sent.
 */
export function getApiTarget() {
  if (typeof window !== 'undefined') {
    const storedTarget = localStorage.getItem(STORAGE_KEY);

    if (storedTarget && isValidTarget(storedTarget)) {
      return storedTarget;
    }
  }

  return null;
}

/**
 * Get the API target header object for fetch/axios requests.
 * Returns { 'X-API-Target': target } if set, or {} if not.
 */
export function getApiTargetHeader() {
  const target = getApiTarget();

  if (target) {
    return { [HEADER_NAME]: target };
  }

  return {};
}

/**
 * Set the API target in localStorage.
 * Utility for programmatic access (e.g. test setup).
 */
export function setApiTarget(target) {
  if (typeof window !== 'undefined') {
    if (isValidTarget(target)) {
      localStorage.setItem(STORAGE_KEY, target);
    } else {
      // eslint-disable-next-line no-console
      console.warn(
        `Invalid API target: ${target}. Valid values: ${VALID_TARGETS.join(
          ', ',
        )}`,
      );
    }
  }
}

/**
 * Clear the API target from localStorage.
 */
export function clearApiTarget() {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(STORAGE_KEY);
  }
}

/**
 * Determine whether the Amplify (new SR Portal) path should be used.
 *
 * When a QA override is set via localStorage:
 *   - 'amplify'     → honour the provider's useNewSRPortal flag
 *   - 'amplify-all' → force Amplify on for all clinics
 *   - 'cxc'         → force Amplify off
 *
 * Otherwise falls back to the provider-level flag.
 *
 * @param {boolean} [providerFlag=false] - The provider's useNewSRPortal value
 * @returns {boolean}
 */
export function shouldUseAmplify(providerFlag = false) {
  const target = getApiTarget();
  if (target === 'amplify-all') return true;
  if (target === 'cxc') return false;
  return providerFlag;
}
